import {
    Alert,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    FormControlLabel,
    Stack,
    Typography,
} from "@mui/material"
import { ReactElement, useContext, useState } from "react"

import { useAppDispatch } from "../../../hooks/useDispatch"
import { setShowAccountSetUp } from "../../../redux/preferencesSlice"
import { SUPPRESSED } from "../../../redux/types"
import useRejectInvite from "../../../services/middleware/hooks/account/useRejectInvite"
import { snackbar } from "../../../utils/general/generalUtils"
import PolicyBuilder from "../../../webpages/privacyPage/policyBuilder"
import { TERMS_OF_USE_CONTENT } from "../../../webpages/privacyPage/termsOfUse"
import AppButton from "../../buttons/appButton"
import AppLoadingButton from "../../buttons/appLoadingButton"
import CheckMarkList from "../../dataDisplay/lsits/checkMarkList"
import { BUTTON_SPACING } from "../common/config"
import FullScreenTitleContent from "../common/fullScreenTitleContent"
import { AccountSetUpContext } from "../providers/firstLoginProvider"
import { INVITE_ARE_YOU_SURE } from "../screenDispatchers/firstLoginConstants"

export const ITEMS_INSTALLER = [
    "Your account will be transferred into their team",
    "The team’s admins will be able to administer your account",
    "Your existing applications will be visible to their admins",
    "No changes will be made to your existing applications",
]

export const ITEMS_MANUFACTURER = [
    "Your account will be transferred into their team",
    "The team’s admins will be able to administer your account",
    "You will become a manufacturer user, meaning your can register and manage devices produced by your company",
]

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Unreachable code error
// eslint-disable-next-line no-undef
const CONFIG = GLOBAL_CONFIG

/** Screen to review an invite */
const InviteReview = (): ReactElement => {
    const { setCurrentScreen, invite } = useContext(AccountSetUpContext)
    const { rejectInvite, isRejecting, error } = useRejectInvite()
    const [showTerms, setShowTerms] = useState(false)
    const [termsAccepted, setTermsAccepted] = useState(false)
    const dispatch = useAppDispatch()

    return (
        <>
            <FullScreenTitleContent
                titlePrimaryColor="You've been invited to join "
                titleMain={`${invite?.organisationName}`}
                subtitle={`${invite?.organisationName} has been created on ENA Connect Direct. The team's admin, ${invite?.inviter}, has invited you to join them!`}
                content={
                    <>
                        <Typography
                            variant="h6"
                            fontWeight={400}
                            color="text.secondary"
                        >
                            With this account, you can:
                        </Typography>

                        <CheckMarkList
                            secondary
                            variant="h6"
                            fontWeight={400}
                            items={
                                invite?.organisationType === "installer"
                                    ? ITEMS_INSTALLER
                                    : ITEMS_MANUFACTURER
                            }
                        />
                        <Typography
                            variant="h6"
                            fontWeight={400}
                            color="text.secondary"
                        >
                            You can accept, reject or defer. If you defer, we
                            will prompt you next time you access the site
                        </Typography>
                    </>
                }
                actionContent={
                    <Stack width="100%" spacing={BUTTON_SPACING}>
                        <FormControlLabel
                            sx={{ width: "100%" }}
                            control={
                                <Checkbox
                                    checked={termsAccepted}
                                    onChange={(event) =>
                                        setTermsAccepted(event.target.checked)
                                    }
                                />
                            }
                            label={
                                <Typography>
                                    I have read and accept our{" "}
                                    <AppButton
                                        type="button"
                                        onClick={() => {
                                            setShowTerms(true)
                                        }}
                                        sx={{
                                            p: 0,
                                            textDecoration: "underline",
                                        }}
                                    >
                                        Terms of Use
                                    </AppButton>
                                </Typography>
                            }
                        />
                        <Collapse in={!!error}>
                            <Stack width="100%" spacing={BUTTON_SPACING}>
                                <Alert severity="warning">
                                    {error?.displayDetail}
                                </Alert>
                                <AppButton
                                    fullWidth
                                    variant="outlined"
                                    color="warning"
                                    onClick={() =>
                                        window.open(
                                            CONFIG.SN_PORTAL_URL,
                                            "_blank"
                                        )
                                    }
                                    disabled={isRejecting}
                                >
                                    Support Portal
                                </AppButton>
                            </Stack>
                        </Collapse>
                        <AppButton
                            fullWidth
                            variant="contained"
                            disabled={isRejecting || !termsAccepted}
                            onClick={() => {
                                setCurrentScreen(INVITE_ARE_YOU_SURE)
                            }}
                        >
                            Accept
                        </AppButton>
                        <AppLoadingButton
                            fullWidth
                            variant="contained"
                            color="error"
                            disabled={isRejecting || !termsAccepted}
                            loading={isRejecting}
                            onClick={rejectInvite}
                        >
                            Reject
                        </AppLoadingButton>
                        <AppButton
                            fullWidth
                            variant="outlined"
                            disabled={isRejecting || !termsAccepted}
                            onClick={() => {
                                dispatch(setShowAccountSetUp(SUPPRESSED))
                                snackbar(
                                    "Decision deferred. We will re-prompt you when you next access the portal",
                                    "default"
                                )
                            }}
                        >
                            I need more time
                        </AppButton>
                    </Stack>
                }
            />
            <Dialog
                open={showTerms}
                onClose={() => setShowTerms(false)}
                aria-labelledby="terms of use modal"
                aria-describedby="pop up modal for terms of use"
            >
                <DialogContent>
                    <PolicyBuilder
                        title="ENA Connect Direct Terms of Use"
                        body={TERMS_OF_USE_CONTENT}
                    />
                </DialogContent>
                <DialogActions>
                    <AppButton
                        variant="outlined"
                        sx={{ m: 1 }}
                        onClick={() => {
                            setShowTerms(false)
                        }}
                    >
                        Close
                    </AppButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default InviteReview
